import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Oppdaterer nettleserfanens tittel (document.title) basert på gjeldende rute,
 * slik at brukeren enkelt finner riktig fane når mange er åpne samtidig.
 *
 * Formatet er "<Sidenavn> · RetailMaster", eller bare "RetailMaster" for ruter
 * som ikke har et navn registrert.
 */

// path → lesbart norsk navn. Støtter både eksakt og prefix-match.
const ROUTE_NAMES = [
  { path: '/', exact: true, name: 'Dashboard' },
  { path: '/login', exact: true, name: 'Logg inn' },
  { path: '/setup-first-user', exact: true, name: 'Førstegangsoppsett' },

  // Varebehandling
  { path: '/varebehandling', exact: true, name: 'Varebehandling' },
  { path: '/produktmaster', name: 'Produktmaster' },
  { path: '/varemottak', name: 'Varemottak' },
  { path: '/varetelling', name: 'Varetelling' },
  { path: '/periodisk-varetelling', name: 'Periodisk varetelling' },
  { path: '/lagerstyring', name: 'Lagerstyring' },
  { path: '/bare-pa-lager', name: 'Bare på lager' },
  { path: '/ordrebekreftelse', name: 'Ordrebekreftelse' },
  { path: '/varer-i-bestilling', name: 'Varer i bestilling' },
  { path: '/kundebestilling', name: 'Kundebestilling' },
  { path: '/salg', name: 'Salg' },
  { path: '/outlet', name: 'Outlet' },
  { path: '/nyheter', name: 'Nyheter' },
  { path: '/kasseoppgjor', name: 'Kasseoppgjør' },
  { path: '/kassesamspill', name: 'Kassesamspill' },
  { path: '/pck-import', name: 'PCK-import' },
  { path: '/missing-fields-checker', name: 'Manglende felt' },

  // Ordrebehandling
  { path: '/ordrebehandling', exact: true, name: 'Ordrebehandling' },
  { path: '/publiseringsplan', name: 'Publiseringsplan' },

  // Oppskrifter
  { path: '/oppskriftsbibliotek', name: 'Oppskriftsbibliotek' },
  { path: '/oppskrift/', name: 'Oppskrift' },
  { path: '/oppskrift-innsjekk', name: 'Oppskrift-innsjekk' },

  // Innstillinger
  { path: '/innstillinger/dropbox', exact: true, name: 'Dropbox-innstillinger' },
  { path: '/innstillinger', name: 'Innstillinger' },
  { path: '/leverandor-oppsett', name: 'Leverandør-oppsett' },
  { path: '/synkronisering', name: 'Synkronisering' },
  { path: '/woocommerce-sync', name: 'WooCommerce-sync' },
  { path: '/agent-notater', name: 'Agent-notater' },
  { path: '/brukere', name: 'Brukere' },

  // Import
  { path: '/excel-import', name: 'Excel-import' },
  { path: '/import/rapporter', exact: true, name: 'Importrapporter' },
  { path: '/import/web', exact: true, name: 'Web-import' },
  { path: '/import/tilbehor', exact: true, name: 'Tilbehør-import' },
  { path: '/import/excel', exact: true, name: 'Excel-import' },
  { path: '/import', exact: true, name: 'Import' },
  { path: '/product-import', name: 'Produktimport' },
  { path: '/product-kobling', name: 'Produktkobling' },
  { path: '/importer-varer', name: 'Importer varer' },
  { path: '/sandnes-import', name: 'Sandnes-import' },
  { path: '/rauma-import', name: 'Rauma-import' },
  { path: '/hoy-import', name: 'Hoy-import' },
  { path: '/hillesvag-import', name: 'Hillesvåg-import' },
  { path: '/viking-import', name: 'Viking-import' },
  { path: '/filcolana-import', name: 'Filcolana-import' },
  { path: '/isager-import', name: 'Isager-import' },
  { path: '/permin-import', name: 'Permin-import' },
  { path: '/villy-import', name: 'Villy Jensen-import' },
  { path: '/kortoggodt-import', name: 'Kort og godt-import' },

  // Garnpakker
  { path: '/garnpakker', name: 'Garnpakker' },
];

function resolveName(pathname) {
  // Eksakt match først
  const exact = ROUTE_NAMES.find(r => r.exact && r.path === pathname);
  if (exact) return exact.name;
  // Prefix (lengste treff vinner)
  const prefix = ROUTE_NAMES
    .filter(r => !r.exact && pathname.startsWith(r.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return prefix?.name || null;
}

export default function DocumentTitleUpdater() {
  const { pathname } = useLocation();

  useEffect(() => {
    const name = resolveName(pathname);
    document.title = name ? `${name} · RetailMaster` : 'RetailMaster';
  }, [pathname]);

  return null;
}
